import { MKPlayerEvent } from "../../enums";
import { MKPlayerEventBase, MKPlayerEventCallback } from "../../interfaces";

/**
 * MKEvent Base class.
 * @internal
 */
export class MKEventBase {
    private events: {[eventType: string]: MKPlayerEventCallback<any>[]};

    constructor() {
        this.events = {};
    }

    /**
     * Subscribes an event handler to a player event.
     * 
     * @param eventType The type of event to subscribe to.
     * 
     * @param callback The event callback handler that will be called when the event fires.
     */
    on<T extends MKPlayerEventBase>(eventType: MKPlayerEvent, callback: MKPlayerEventCallback<T>): void {
        if (!this.events[eventType]) {
            this.events[eventType] = [];
        }
        this.events[eventType].push(callback);
    }

    /**
     * Removes a handler for a player event.
     * 
     * @param eventType The event to remove the handler from.
     * 
     * @param callback The callback handler to remove.
     */
    off<T extends MKPlayerEventBase>(eventType: MKPlayerEvent, callback: MKPlayerEventCallback<T>): void {
        if (this.events[eventType]) {
            this.events[eventType] = (this.events[eventType] as MKPlayerEventCallback<T>[]).filter((eventCallback: MKPlayerEventCallback<T>) => eventCallback !== callback);
        }
    }

    /**
     * Removes all event handlers registered for the given event type.
     *
     * @param eventType The event for which all event handlers are to be removed.
     */
    offAll(eventType: MKPlayerEvent): void {
        if (this.events[eventType]) {
            delete this.events[eventType];
        }
    }

    /**
     * Emit the event for the given eventType with the eventData if specified.
     * @internal
     * 
     * @param eventType The type of the event.
     * 
     * @param event The event itself.
     */
    emit<T extends MKPlayerEventBase>(eventType: MKPlayerEvent, event: T): void {
        if (this.events[eventType]) {
            this.events[eventType].forEach((callback) => callback(event));
        }
    }
}